import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle, Clock, CheckCircle, Calendar, FileText,
  ShoppingCart, Package, Briefcase, Receipt, Plane
} from "lucide-react";

const API = process.env.REACT_APP_API || "";

export function MeinTagView({ setTab }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("heute");
  const [loading, setLoading] = useState(true);
  const [guardianSignals, setGuardianSignals] = useState([]);

  const headers = useCallback(() => {
    const t = localStorage.getItem("token") || sessionStorage.getItem("token");
    return { Authorization: `Bearer ${t}` };
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const [r, guardian] = await Promise.all([
        axios.get(`${API}/api/crossstudio/mein-tag`, { headers: headers(), params: { filter } }),
        axios.get(`${API}/api/guardian/signals`, { headers: headers(), params: { limit: 3, refresh: true } }),
      ]);
      setItems(r.data.items || []);
      setGuardianSignals(guardian.data.signals || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [headers, filter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const prioColor = (p) => ({
    hoch: "border-l-red-500 bg-red-50",
    mittel: "border-l-orange-400 bg-orange-50",
    normal: "border-l-blue-400 bg-blue-50",
    niedrig: "border-l-gray-300 bg-gray-50",
  }[p] || "border-l-gray-300");

  const typIcon = (typ) => {
    if (typ.includes("Rechnung")) return <Receipt className="w-4 h-4 text-red-500" />;
    if (typ.includes("Aufgabe")) return <CheckCircle className="w-4 h-4 text-blue-500" />;
    if (typ.includes("Bestellung")) return <ShoppingCart className="w-4 h-4 text-pink-500" />;
    if (typ.includes("Vertrag") || typ.includes("Frist")) return <FileText className="w-4 h-4 text-orange-500" />;
    if (typ.includes("Beleg")) return <Receipt className="w-4 h-4 text-gray-500" />;
    if (typ.includes("Einsatz")) return <Briefcase className="w-4 h-4 text-purple-500" />;
    return <Clock className="w-4 h-4 text-gray-400" />;
  };

  const filters = [
    { id: "heute", label: "Heute" },
    { id: "woche", label: "Diese Woche" },
    { id: "ueberfaellig", label: "Überfällig" },
  ];

  const navigate = (item) => {
    if (item.link && setTab) setTab(item.link);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Mein Tag</h2>
        <div className="flex gap-1">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1 rounded-md text-sm ${filter === f.id ? "bg-[var(--brand-primary)] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {!loading && guardianSignals.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 p-4" data-testid="mein-tag-guardian">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="flex items-center gap-2 font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" /> Business Guardian</h3>
              <p className="mt-1 text-sm text-amber-800">Diese Punkte wurden aus vorhandenen Daten abgeleitet und benötigen eine bewusste Prüfung.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setTab && setTab("business-cockpit")}>Im Cockpit prüfen</Button>
          </div>
          <div className="mt-3 space-y-2">
            {guardianSignals.map((signal) => (
              <button
                key={signal.id}
                type="button"
                onClick={() => signal.target?.tab && setTab && setTab(signal.target.tab)}
                className="block w-full rounded-md border border-amber-200 bg-white p-2 text-left text-sm hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <span className="font-medium text-gray-800">{signal.title}</span>
                <span className="mt-0.5 block text-xs text-gray-600">{signal.description}</span>
              </button>
            ))}
          </div>
        </Card>
      )}
      {loading ? (
        <p className="text-sm text-gray-400">Lade Aufgaben...</p>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center">
          <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <p className="text-gray-500">Alles erledigt! Keine offenen Punkte.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((item, i) => (
            <Card
              key={i}
              className={`p-3 border-l-4 cursor-pointer hover:shadow-sm transition ${prioColor(item.prioritaet)}`}
              onClick={() => navigate(item)}
            >
              <div className="flex items-center gap-3">
                {typIcon(item.typ)}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{item.titel}</div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{item.typ}</span>
                    <span className="text-gray-300">|</span>
                    <span>{item.studio}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-xs font-medium ${item.prioritaet === "hoch" ? "text-red-600" : "text-gray-500"}`}>
                    {item.faellig}
                  </div>
                  <div className={`text-[10px] px-1.5 py-0.5 rounded mt-0.5 inline-block ${
                    item.prioritaet === "hoch" ? "bg-red-100 text-red-700" :
                    item.prioritaet === "mittel" ? "bg-orange-100 text-orange-700" :
                    "bg-gray-100 text-gray-600"
                  }`}>{item.prioritaet}</div>
                </div>
              </div>
            </Card>
          ))}
          <p className="text-xs text-gray-400 text-center pt-2">{items.length} offene Punkte</p>
        </div>
      )}
    </div>
  );
}
